import React, { useEffect } from 'react';
import { Link, useParams, Navigate, useNavigate } from 'react-router-dom';
import PageShell from './PageShell';
import Seo from '../components/Seo';
import ProductGallery from '../components/ProductGallery';
import { useI18n } from '../context/I18nContext';
import { useCart } from '../context/CartContext';
import { useToast } from '../hooks/use-toast';
import { publicApi } from '../api';
import { useApi } from '../hooks/useApi';
import { staticImg, fmtMoney, stripHtml } from '../utils/media';
import { Spinner } from '../components/StateHelpers';
import { ShieldCheck, Zap, Download, ShoppingCart, ArrowLeft, CheckCircle2, CreditCard } from 'lucide-react';

export default function ProductDetail() {
  const { slug } = useParams();
  const { lang, tr } = useI18n();
  const { add } = useCart();
  const { toast } = useToast();
  const nav = useNavigate();
  const { data: p, loading, error } = useApi(() => publicApi.product(slug, lang), [slug, lang]);
  useEffect(() => { if (p) document.title = `${p.name} — Aegis Studios`; }, [p]);
  if (loading) return <PageShell eyebrow="" title="..."><Spinner /></PageShell>;
  if (error) return <Navigate to="/products" replace />;
  if (!p) return <Navigate to="/products" replace />;

  const addToCart = () => {
    add(p, 1);
    toast({ title: 'Sepete eklendi ✓', description: p.name });
  };
  const buyNow = () => {
    add(p, 1);
    nav('/checkout');
  };

  return (
    <PageShell eyebrow={(p.category_name || '').toUpperCase()} title={<span className="text-white">{p.name}</span>} subtitle={stripHtml(p.short_description)}>
      <Seo title={p.meta_title || p.name} description={p.meta_description || p.short_description} image={staticImg(p.image)} />
      <section className="pb-24">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-10">
          <Link to="/products" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-[#f4d896] mb-8">
            <ArrowLeft className="w-4 h-4" /> {tr('product.back', 'Tüm ürünlere dön')}
          </Link>
          <div className="grid lg:grid-cols-[1.1fr_1fr] gap-8 lg:gap-10">
            <div>
              <ProductGallery mainImage={p.image} images={p.gallery || []} alt={p.name} />
              {(p.description_html || p.description) ? (
                <div className="mt-8 md:mt-10 overflow-x-auto max-w-full">
                  <article
                    className="prose prose-invert prose-p:text-white/70 prose-headings:text-white prose-strong:text-white max-w-none prose-pre:bg-[#0f0e12] prose-pre:border prose-pre:border-white/10 prose-img:rounded-xl prose-img:border prose-img:border-white/10 prose-img:my-6 prose-img:max-w-full prose-img:h-auto prose-table:overflow-x-auto"
                    ref={(el) => {
                      if (!el) return;
                      // Hide any broken images gracefully
                      el.querySelectorAll('img').forEach((img) => {
                        img.setAttribute('loading', 'lazy');
                        img.onerror = () => { img.style.display = 'none'; };
                      });
                    }}
                    dangerouslySetInnerHTML={{ __html: p.description_html || p.description }} />
                </div>
              ) : null}
            </div>
            <aside className="lg:sticky lg:top-24 h-fit mt-8 lg:mt-0">
              <div className="rounded-3xl border border-[#e4b44e]/30 bg-gradient-to-b from-[#1a1710] to-[#0a090c] p-6 md:p-8 shadow-[0_20px_60px_-20px_rgba(228,180,78,0.35)]">
                <div className="text-[10px] font-bold tracking-[0.3em] text-[#e4b44e]/85 mb-2">{(p.category_name || '').toUpperCase()}</div>
                <h2 className="font-display text-2xl md:text-3xl font-black text-white mb-4">{p.name}</h2>
                <div className="flex items-baseline gap-3 mb-6 flex-wrap">
                  {p.old_price ? <span className="text-white/40 line-through text-lg">{fmtMoney(p.old_price)} ₺</span> : null}
                  <span className="font-display text-4xl md:text-5xl font-black gold-text">{fmtMoney(p.price)} ₺</span>
                </div>
                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-3 text-sm text-white/80">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" /> {tr('product.stock', 'Stok')}: {
                      (p.show_stock === 1 || p.show_stock === true || p.show_stock === '1' || p.show_stock === 'true')
                        ? (p.stock > 0 ? `${p.stock} adet` : tr('product.out_of_stock', 'Yok'))
                        : (p.stock > 0 ? tr('product.in_stock', 'Stokta Var') : tr('product.out_of_stock', 'Yok'))
                    }
                  </div>
                  <div className="flex items-center gap-3 text-sm text-white/80"><Zap className="w-4 h-4 text-[#e4b44e]" /> {tr('product.instant', 'Anında dijital teslimat')}</div>
                  <div className="flex items-center gap-3 text-sm text-white/80"><ShieldCheck className="w-4 h-4 text-[#e4b44e]" /> {tr('product.licensed', 'Lisanslı & güvenli')}</div>
                  <div className="flex items-center gap-3 text-sm text-white/80"><Download className="w-4 h-4 text-[#e4b44e]" /> {tr('product.setup', 'Kurulum desteği dahil')}</div>
                </div>
                <button onClick={addToCart} className="w-full h-12 rounded-full btn-gold inline-flex items-center justify-center gap-2 text-sm font-extrabold tracking-widest">
                  <ShoppingCart className="w-4 h-4" /> {tr('product.add_to_cart', 'SEPETE EKLE')}
                </button>
                <button onClick={buyNow} className="mt-3 w-full h-12 rounded-full btn-outline-gold inline-flex items-center justify-center gap-2 text-xs font-bold tracking-widest">
                  <CreditCard className="w-4 h-4" /> HEMEN SATIN AL
                </button>
                <Link to="/contact" className="mt-3 w-full h-11 rounded-full text-white/60 hover:text-[#f4d896] inline-flex items-center justify-center text-xs font-bold tracking-widest transition">{tr('product.demo', 'DEMO / SORU SOR')}</Link>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
